import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Phone, ChevronRight, ArrowRight, AlertTriangle, CheckCircle, MessageCircle, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { companyInfo, serviceAreas } from '../data/mock';
import { getMoldSubservice, getAllMoldSubservices } from '../data/moldSubserviceData';
import SEOHead from '../components/SEOHead';
import MoldLeadForm from '../components/MoldLeadForm';
import MoldStickyCTA from '../components/MoldStickyCTA';
import TextLink from '../components/TextLink';

const MoldSubservicePage = () => {
  const { subserviceSlug } = useParams();
  const subservice = getMoldSubservice(subserviceSlug);
  const allSubservices = getAllMoldSubservices();

  if (!subservice) {
    return <Navigate to="/services/mold-remediation" replace />;
  }

  const otherSubservices = allSubservices.filter(s => s.slug !== subservice.slug);

  const scrollToForm = () => {
    const form = document.getElementById('mold-subservice-form');
    if (form) {
      form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Schema markup
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `https://graycontractingdfw.com/services/mold-remediation/${subservice.slug}#service`,
    "name": `${subservice.title} in Dallas-Fort Worth`,
    "url": `https://graycontractingdfw.com/services/mold-remediation/${subservice.slug}`,
    "serviceType": subservice.title,
    "provider": {
      "@id": "https://graycontractingdfw.com/#organization"
    },
    "areaServed": serviceAreas.slice(0, 5).map(area => ({
      "@type": "City",
      "name": area,
      "containedInPlace": { "@type": "State", "name": "Texas" }
    })),
    "description": subservice.metaDescription
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://graycontractingdfw.com" },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://graycontractingdfw.com/services" },
      { "@type": "ListItem", "position": 3, "name": "Mold Remediation", "item": "https://graycontractingdfw.com/services/mold-remediation" },
      { "@type": "ListItem", "position": 4, "name": subservice.title, "item": `https://graycontractingdfw.com/services/mold-remediation/${subservice.slug}` }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": subservice.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <div>
      <SEOHead 
        title={subservice.metaTitle}
        description={subservice.metaDescription}
        keywords={subservice.keywords}
        canonical={`/services/mold-remediation/${subservice.slug}`}
      />
      
      {/* Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* Hero Section */}
      <section className="relative py-20 lg:py-24 bg-neutral-900">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=2000')] bg-cover bg-center opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-900 via-neutral-900/95 to-neutral-900/80" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-neutral-400 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-amber-400">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/services" className="hover:text-amber-400">Services</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/services/mold-remediation" className="hover:text-amber-400">Mold Remediation</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-amber-400">{subservice.title}</span>
          </nav>

          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <Shield className="w-4 h-4" />
            Specialized Mold Remediation Service
          </div>
          
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {subservice.h1}
            </h1>
            <p className="text-xl text-neutral-300 mb-8 leading-relaxed">
              {subservice.heroText}
            </p>
            
            {/* Primary CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <a href="tel:+12149087644">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-6 text-lg w-full sm:w-auto">
                  <Phone className="mr-2 w-5 h-5" />
                  Call 214-908-7644
                </Button>
              </a>
              <TextLink>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-neutral-900 font-semibold px-8 py-6 text-lg w-full sm:w-auto">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  Send Photos
                </Button>
              </TextLink>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-white font-semibold px-8 py-6 text-lg w-full sm:w-auto"
                onClick={scrollToForm}
              >
                Request Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Warning/Info Note */}
      <section className="py-8 bg-amber-50 border-b border-amber-200">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-amber-800 text-sm leading-relaxed">
              {subservice.warningNote}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Signs Section */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-6">
                  Signs You May Need {subservice.title}
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {subservice.signs.map((sign, i) => (
                    <div key={i} className="flex items-start gap-3 bg-neutral-50 p-4 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <span className="text-neutral-700 text-sm">{sign}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Process Section */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-6">
                  {subservice.title} Process
                </h2>
                <div className="space-y-4">
                  {subservice.process.map((step) => (
                    <div key={step.step} className="flex gap-4 bg-neutral-50 p-5 rounded-xl">
                      <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">{step.step}</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-neutral-900 mb-1">{step.title}</h3>
                        <p className="text-neutral-600 text-sm">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQs */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-6">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="w-full">
                  {subservice.faqs.map((faq, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border-b border-neutral-200">
                      <AccordionTrigger className="text-left font-semibold text-neutral-900 hover:text-amber-600 py-4">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-neutral-600 pb-4">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              {/* Related Services */}
              <div>
                <h2 className="text-2xl font-bold text-neutral-900 mb-6">
                  Other Mold Services
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {otherSubservices.map((service) => (
                    <Link 
                      key={service.slug}
                      to={`/services/mold-remediation/${service.slug}`}
                      className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg hover:bg-amber-50 hover:border-amber-300 border border-neutral-200 transition-all group"
                    >
                      <span className="font-medium text-neutral-700 group-hover:text-amber-600">{service.title}</span>
                      <ArrowRight className="w-4 h-4 text-neutral-400 group-hover:text-amber-600" />
                    </Link>
                  ))}
                  <Link 
                    to="/services/mold-remediation"
                    className="flex items-center justify-between p-4 bg-amber-500 rounded-lg hover:bg-amber-600 transition-all group"
                  >
                    <span className="font-medium text-white">All Mold Services</span>
                    <ArrowRight className="w-4 h-4 text-white" />
                  </Link>
                </div>
              </div>

              {/* Service Areas */}
              <div>
                <h2 className="text-2xl font-bold text-neutral-900 mb-4">
                  {subservice.title} Service Areas
                </h2>
                <p className="text-neutral-600 mb-4">
                  Gray Contracting provides {subservice.title.toLowerCase()} services throughout Dallas-Fort Worth:
                </p>
                <div className="flex flex-wrap gap-2">
                  {serviceAreas.map((area, i) => (
                    <Link
                      key={i}
                      to={`/services/mold-remediation/${area.toLowerCase().replace(/\s+/g, '-')}`}
                      className="px-4 py-2 bg-neutral-100 hover:bg-amber-100 rounded-full text-sm text-neutral-700 hover:text-amber-700 transition-colors"
                    >
                      {area}, TX
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar - Lead Form */}
            <div className="lg:col-span-1">
              <div className="sticky top-24" id="mold-subservice-form">
                <MoldLeadForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-r from-amber-500 to-amber-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Need {subservice.title} in Dallas-Fort Worth?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Contact Gray Contracting to discuss your situation and understand the next step.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+12149087644">
              <Button size="lg" className="bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-8 py-6 text-lg">
                <Phone className="mr-2 w-5 h-5" />
                Call 214-908-7644
              </Button>
            </a>
            <Link to="/services/mold-remediation">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-amber-600 font-semibold px-8 py-6 text-lg">
                View All Mold Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      <MoldStickyCTA onEstimateClick={scrollToForm} />
    </div>
  );
};

export default MoldSubservicePage;
